/**
 * useShowToastOnClientChange hook
 *
 * Displays a toast notification if a pending toast configuration is found in localStorage.
 * Mostly used on client change to notify the user because of the page reload.
 * Cleans up the pending toast key after a delay to prevent repeated notifications.
 * Uses a timeout to allow the toast to be shown before cleanup.
 *
 * @returns {void}
 */
import { toast } from '@app/components/ui/Toast/toast'
import { logger } from '@app/utils/logger'
import { useEffect } from 'react'

const PENDING_TOAST_KEY = 'pending_toast'
const TOAST_CLEANUP_DELAY = 5000

export const useShowToastOnClientChange = () => {
  useEffect(() => {
    const pendingToast = localStorage.getItem(PENDING_TOAST_KEY)

    if (!pendingToast) return

    try {
      const toastConfig = JSON.parse(pendingToast)
      toast(toastConfig)

      // timeout to allow toast to be displayed before cleanup
      const timeoutId = setTimeout(() => {
        localStorage.removeItem(PENDING_TOAST_KEY)
      }, TOAST_CLEANUP_DELAY)

      // cleanup timeout if component unmounts
      return () => clearTimeout(timeoutId)
    } catch (error) {
      logger.error('Failed to show pending toast:', error)
      localStorage.removeItem(PENDING_TOAST_KEY)
    }
  }, [])
}
